/*
 * The Slack half of Settings → Notifications — Slack build step 4 of `DESIGN-slack.md`.
 *
 * The Teams half's twin (`DigestTeamsRow.jsx`), in the same two pieces:
 *
 *   <SlackChannelPicker>  — ONE channel for the company, at the top of the tab, under the
 *                           Teams one. Rob's A1 (18/10/2026): chosen here, beside the other
 *                           digest settings, not on the Integrations card.
 *   <DigestSlackRow>      — "Also post it in Slack", one per digest card (Rob's C1: all three).
 *
 * ⚠️ **NOTHING HERE APPEARS FOR A COMPANY THAT HASN'T CONNECTED SLACK.** Unlike the Teams
 * picker, which explains how to add the app: a company without Slack should see no Slack
 * noise on a tab it uses every week. Connecting lives on the Integrations card.
 *
 * ⚠️ **THE TOGGLE AND THE DIGEST ARE INDEPENDENT — ROB'S DECISION (a), KEPT FOR SLACK.**
 * Choosing a channel never switches a digest on, so each row SAYS when nothing will post.
 *
 * ⚠️ **PRIVATE CHANNELS (Rob's B1).** Slack doesn't tell us a private channel exists until
 * HolidayHive has been invited into it, so it can't be listed greyed out. The picker says how
 * to invite it, and Refresh list fetches again.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Hash, Loader2, RefreshCw } from 'lucide-react';
import { slackApi } from '../lib/api';
import { formatDateGB } from '../lib/dates';
import { channelLabel, postProblemText } from '../lib/slackDigest';

/*
 * ⚠️ Loaded once for the tab and handed down — the Teams hook's rule. Three digest cards
 * each asking Slack for the channel list would be three Tier 2 calls for one answer.
 *
 * `slack` is null while loading, `{ connected: false }` for a company without Slack, and the
 * server's answer otherwise. A failed request reads as not connected: the tab must still work.
 */
export function useSlackChannels() {
  const [slack, setSlack] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(() => slackApi
    .channels()
    .then((res) => setSlack(res.data || { connected: false }))
    .catch(() => setSlack({ connected: false })), []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try { await load(); } finally { setRefreshing(false); }
  };

  return { slack, refresh, refreshing };
}

export function SlackChannelPicker({ slack, value, onChange, onRefresh, refreshing }) {
  if (!slack || !slack.connected) return null;

  const channels = slack.channels || [];
  const chosen = slack.chosen;
  const digest = slack.digest || {};
  /*
   * ⚠️ **THE CHOSEN CHANNEL STAYS VISIBLE WHEN SLACK NO LONGER LISTS IT.** Archived, deleted,
   * or HolidayHive removed from it: without this option the select would show "No channel
   * chosen" while posts were failing — the screen telling a comfortable lie (Rob's D1).
   */
  const missing = chosen && chosen.available === false && !channels.some((c) => c.id === chosen.id)
    ? chosen : null;
  // ⚠️ The warning belongs to the channel it happened in — not to one picked a moment ago.
  const showProblem = !!digest.error && !!value && value === digest.channel_id;

  return (
    <div data-testid="slack-channel-picker">
      <label className="text-sm font-medium mb-2 flex items-center gap-1.5" htmlFor="slack-channel-select">
        <Hash className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
        Post who&rsquo;s off in Slack
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <select
          id="slack-channel-select"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 px-3 rounded-md border border-input bg-background text-sm w-full max-w-sm"
          data-testid="slack-channel-select"
        >
          {/* ⚠️ An explicit "not chosen", as in the Teams picker: otherwise the first channel
              looks selected when nothing is. */}
          <option value="">No channel chosen</option>
          {missing && (
            <option value={missing.id}>
              {channelLabel(missing)} &mdash; can&rsquo;t post here any more
            </option>
          )}
          {channels.map((c) => (
            <option key={c.id} value={c.id}>{channelLabel(c)}</option>
          ))}
        </select>
        <button type="button" onClick={onRefresh} disabled={refreshing}
                className="text-xs inline-flex items-center gap-1.5 underline text-muted-foreground hover:text-foreground disabled:opacity-50"
                data-testid="slack-channel-refresh">
          {refreshing
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
          Refresh list
        </button>
      </div>

      {slack.error && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 mt-2"
           data-testid="slack-channel-list-error">
          Couldn&rsquo;t fetch your Slack channels just now ({slack.error}). Press Refresh list to
          try again.
        </p>
      )}

      {showProblem && (
        <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 mt-2"
             data-testid="slack-channel-problem">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-xs text-amber-800">
            The last who&rsquo;s-off post to {channelLabel({ id: digest.channel_id, name: digest.channel_name, private: digest.private })}
            {' '}didn&rsquo;t go{digest.error_at ? ` on ${formatDateGB(digest.error_at)}` : ''}:
            {' '}{postProblemText(digest.error)}. It will try again at the next post.
          </p>
        </div>
      )}

      {!showProblem && value && value === digest.channel_id && digest.posted_at && (
        <p className="text-xs text-muted-foreground mt-2" data-testid="slack-channel-last-posted">
          Last posted {formatDateGB(digest.posted_at)}.
        </p>
      )}

      <p className="text-xs text-muted-foreground mt-2">
        One channel for the whole company &mdash; the daily, weekly and monthly digests all post
        here. Everyone in the channel sees it, whoever the email settings are for.
      </p>
      <p className="text-xs text-muted-foreground mt-1" data-testid="slack-channel-private-hint">
        Private channel? In Slack, type <span className="font-mono">/invite @HolidayHive</span> in
        it, then press Refresh list.
      </p>
    </div>
  );
}

export default function DigestSlackRow({ kind, enabled, digestEnabled, channelId, slack, onChange }) {
  if (!slack || !slack.connected) return null;

  const hasChannel = !!channelId;
  const canPost = hasChannel && digestEnabled;

  return (
    <div className="border-t border-border mt-4 pt-4" data-testid={`digest-slack-row-${kind}`}>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm flex items-center gap-2">
          <Hash className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          Also post it in Slack
        </span>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={!!enabled}
            onChange={(e) => onChange(e.target.checked)}
            aria-label={`Also post the ${kind} digest in Slack`}
            data-testid={`digest-slack-toggle-${kind}`}
          />
          <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer
                          peer-checked:after:translate-x-full after:content-[''] after:absolute
                          after:top-0.5 after:left-[2px] after:bg-white after:rounded-full
                          after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
        </label>
      </div>

      {/*
        * ⚠️ The two reasons nothing would post are said separately — they have different fixes
        * (the Teams row's rule, and what makes Rob's decision (a) safe).
        */}
      {enabled && !hasChannel && (
        <p className="text-xs text-amber-700 mt-2" data-testid={`digest-slack-nochannel-${kind}`}>
          Nothing will post yet &mdash; choose a Slack channel at the top of this page.
        </p>
      )}
      {enabled && hasChannel && !digestEnabled && (
        <p className="text-xs text-amber-700 mt-2" data-testid={`digest-slack-nodigest-${kind}`}>
          Nothing will post yet &mdash; this digest is switched off above. Turning it on
          sends the email as well.
        </p>
      )}
      {enabled && canPost && (
        <p className="text-xs text-muted-foreground mt-2">
          Posted at the same time as the email, grouped by department.
        </p>
      )}
    </div>
  );
}
